import {
  type GenericObject,
  type GraphActionEvent,
  type MenuLocationCoords,
  type QuickCreateEntityValue,
  type ToolbarPosition,
} from '@bizzdesign/modeler/models';
import { getBDCoreObjectTypeSubmenu } from '@bizzdesign/shared/utils';
import { type MenuItem } from 'primeng/api';

const contextMenuInitialHeight = 56;
const gapToEdge = 12;

const getRightPanelLeftEdge = (): number => {
  const drawer = document.querySelector<HTMLElement>(
    'bd-drawer.bd-drawer-right:not(.bd-drawer-collapsed)',
  );
  if (!drawer) return window.innerWidth;

  // The HOST element's right edge is always `viewport_width - 12` (position:absolute; right:12px),
  // which is stable throughout the slide animation. The inner content div always has its full CSS
  // width (450px for regular, 400px for small), regardless of the HOST's animated width.
  // Using right - contentWidth gives the correct panel left edge even mid-animation.
  const hostRight = drawer.getBoundingClientRect().right;
  const innerDiv = drawer.querySelector<HTMLElement>('[data-id="drawer"]');
  const contentWidth = innerDiv?.offsetWidth ?? 0;
  if (contentWidth === 0) return window.innerWidth;

  return hostRight - contentWidth;
};

export const isElementCoveredByPanel = (elRect: DOMRect): boolean => {
  // Left panel: element must overlap the panel both horizontally AND vertically.
  // An element that is horizontally aligned with the panel but positioned below
  // its bottom edge is not visually covered and should not be hidden.
  const leftPanel = document.querySelector<HTMLElement>(
    '.p-drawer.bd-sidepanel',
  );
  if (leftPanel) {
    const rect = leftPanel.getBoundingClientRect();
    const panelBottom = rect.bottom + 12;
    if (
      rect.width > 0 &&
      elRect.right <= rect.right &&
      elRect.bottom > rect.top &&
      elRect.top < panelBottom
    ) {
      return true;
    }
  }

  const rightPanelLeftEdge = getRightPanelLeftEdge();
  if (
    rightPanelLeftEdge < window.innerWidth &&
    // if the element's left side is equal or greater than the right-panel's left
    // then that element is covered
    elRect.left >= rightPanelLeftEdge
  )
    return true;

  return false;
};

// distance between element and menu in pixels
export const distanceBetweenMouseAndQuickCreateMenu = 0;
export const distanceBetweenElementAndContextMenu = 48;
// Equal to the tooltip's arrow height, so the arrow spans the gap and its tip
// meets the element - the same relationship primeng's `tooltip.gutter` sets up.
export const distanceBetweenElementAndHyperlinkTooltip = 4;

/**
 * Ids of the floating menus that are kept on-screen by {@link moveToolbar}.
 *
 * @remarks each id must match the DOM id of an element that stays mounted at all
 * times - the element's measured size is required to compute its position.
 */
export type PositionedToolbarId =
  | 'quick-create'
  | 'context-menu'
  | 'hyperlink-tooltip';

/**
 * If a user selects an element, context menu appears for that element
 * If a user holds the left click the quick create menu appears
 * These menus should always be inside of the screen
 * This is used to calculate the position of these menus based on selected element or mouse position
 * @param elementId - to determine what menu id we need to find
 * @param distanceBetweenElementAndMenu - vertical distance between selected element/mouse position and menu
 * @param el - selected element or custom element that has mouse position
 * @returns - an object with top and left values that are used for positioning the menu
 */
export const moveToolbar = (
  elementId: PositionedToolbarId,
  distanceBetweenElementAndMenu: number,
  el?: Element,
): ToolbarPosition | undefined => {
  if (!el) return;
  const element = el.getBoundingClientRect();
  const contextMenu = document.getElementById(elementId);
  if (!contextMenu) return;

  const positionTop = calculateNewTop(
    element,
    contextMenu,
    distanceBetweenElementAndMenu,
    elementId,
  );

  if (positionTop.relativePosition === 'side') {
    const halfWidth = contextMenu.offsetWidth / 2;
    const toolbarWidth = contextMenu.offsetWidth;
    const { left: leftBoundary, right: rightBoundary } =
      getHorizontalBoundaries(positionTop.top, contextMenu.offsetHeight);

    const elementRight = element.left + element.width;
    const rightSideCenter = elementRight + gapToEdge + halfWidth;
    const rightSideValid =
      elementRight + gapToEdge + toolbarWidth + gapToEdge <= rightBoundary;

    const leftSideCenter = element.left - gapToEdge - halfWidth;
    const leftSideValid =
      element.left - gapToEdge - toolbarWidth - gapToEdge >= leftBoundary;

    let left: number;
    if (rightSideValid) {
      left = rightSideCenter;
    } else if (leftSideValid) {
      left = leftSideCenter;
    } else {
      left = Math.max(
        leftBoundary + gapToEdge + halfWidth,
        Math.min(
          rightBoundary - gapToEdge - halfWidth,
          (leftBoundary + rightBoundary) / 2,
        ),
      );
    }

    return {
      top: `${positionTop.top}px`,
      left: `${left}px`,
      relativePosition: 'side',
    };
  }

  return {
    top: `${positionTop.top}px`,
    left: `${calculateNewLeft(element, contextMenu, positionTop.top)}px`,
    relativePosition: positionTop.relativePosition,
  };
};

const getTopBoundary = (): number =>
  document.querySelector('.bd-header')?.getBoundingClientRect().bottom ?? 0;

const calculateNewTop = (
  elRect: DOMRect,
  ctxMenuEl: HTMLElement,
  distanceBetweenElementAndMenu: number,
  elementId: PositionedToolbarId = 'context-menu',
): {
  top: number;
  relativePosition: 'top' | 'bottom' | 'side';
} => {
  let relativePosition: 'top' | 'bottom' | 'side' = 'top';
  const elementIsVisible = window.innerHeight > elRect.top;
  if (!elementIsVisible) {
    /* If the element is not visible, the goal is to hide it by setting its top position
   to the same value as the window's inner height. However, this caused a small issue where
   part of the element's shadow remained visible when the context menu was hidden.
   To resolve this, an additional offset is applied to move the menu further, ensuring
   that the shadow is completely hidden. */
    const BOX_SHADOW_OFFSET = 24;
    return { top: window.innerHeight + BOX_SHADOW_OFFSET, relativePosition };
  }

  if (elementId === 'context-menu') {
    // When the element spans the full visible area (top at or above header, bottom at or below
    // viewport), pin the toolbar just below the header. .bd-header has 12px bottom padding so
    // its getBoundingClientRect().bottom already includes the required clearance gap.
    const elementFillsViewport =
      elRect.top <= getTopBoundary() &&
      elRect.top + elRect.height >= window.innerHeight;
    if (elementFillsViewport) {
      return { top: getTopBoundary(), relativePosition: 'bottom' };
    }
  }

  // The context and quick-create menus have a fixed height; the hyperlink tooltip
  // grows with the URL it shows, so it has to be measured instead.
  const menuHeight =
    elementId === 'hyperlink-tooltip'
      ? ctxMenuEl.offsetHeight
      : contextMenuInitialHeight;

  // Calculate default top position for the context menu with appropriate distance between element and menu
  let top = elRect.top - distanceBetweenElementAndMenu - menuHeight;
  // Check if toolbar would overlap the fixed header.
  // .bd-header has 12px bottom padding so its getBoundingClientRect().bottom
  // already provides the required 12px clearance gap — no extra offset needed.
  const contextMenuNotVisibleOnTop = top < getTopBoundary();
  // Check if there is enough space on the bottom side of the viewport to display the whole menu
  const contextMenuNotVisibleOnBot =
    ctxMenuEl.offsetHeight +
      elRect.top -
      distanceBetweenElementAndMenu -
      menuHeight >
    window.innerHeight;

  if (contextMenuNotVisibleOnTop) {
    const belowTop = elRect.top + elRect.height + distanceBetweenElementAndMenu;
    const belowOverflows =
      (elementId === 'context-menu' || elementId === 'hyperlink-tooltip') &&
      belowTop + ctxMenuEl.offsetHeight > window.innerHeight;

    if (belowOverflows) {
      // Neither above nor below fits — push toolbar to the side of the element.
      const centreTop =
        elRect.top + elRect.height / 2 - ctxMenuEl.offsetHeight / 2;
      const clampedTop = Math.max(
        getTopBoundary(),
        Math.min(centreTop, window.innerHeight - ctxMenuEl.offsetHeight),
      );
      return { top: clampedTop, relativePosition: 'side' };
    }

    relativePosition = 'bottom';
    top = belowTop;
  } else if (contextMenuNotVisibleOnBot) {
    top = elRect.top - ctxMenuEl.offsetHeight - distanceBetweenElementAndMenu;
  }

  return { top, relativePosition };
};

/**
 * Returns the effective horizontal boundaries for a toolbar.
 *
 * A panel imposes a horizontal boundary only when the toolbar vertically
 * overlaps the panel.  When the toolbar is fully above or below the panel
 * no horizontal constraint is applied.
 */
const getLeftBoundary = (toolbarTop: number, toolbarBottom: number): number => {
  const leftPanel = document.querySelector<HTMLElement>(
    '.p-drawer.bd-sidepanel',
  );
  if (!leftPanel) return 0;
  const rect = leftPanel.getBoundingClientRect();
  if (rect.width === 0) return 0;
  const verticalOverlap = toolbarTop < rect.bottom && toolbarBottom > rect.top;
  return verticalOverlap ? rect.right : 0;
};

const getRightBoundary = (
  toolbarTop: number,
  toolbarBottom: number,
): number => {
  const rightDrawer = document.querySelector<HTMLElement>(
    'bd-drawer.bd-drawer-right:not(.bd-drawer-collapsed)',
  );
  if (!rightDrawer) return window.innerWidth;
  const drawerRect = rightDrawer.getBoundingClientRect();
  const innerDiv = rightDrawer.querySelector<HTMLElement>('[data-id="drawer"]');
  const contentWidth = innerDiv?.offsetWidth ?? 0;
  if (contentWidth === 0) return window.innerWidth;
  const panelLeft = drawerRect.right - contentWidth;
  const verticalOverlap =
    toolbarTop < drawerRect.bottom && toolbarBottom > drawerRect.top;
  return verticalOverlap ? panelLeft : window.innerWidth;
};

const getHorizontalBoundaries = (
  toolbarTop: number,
  toolbarHeight: number,
): { left: number; right: number } => {
  const toolbarBottom = toolbarTop + toolbarHeight;
  return {
    left: getLeftBoundary(toolbarTop, toolbarBottom),
    right: getRightBoundary(toolbarTop, toolbarBottom),
  };
};

const calculateNewLeft = (
  elRect: DOMRect,
  ctxMenuEl: HTMLElement,
  toolbarTop: number,
): number => {
  const defaultLeft = elRect.left + elRect.width / 2;
  const halfWidth = ctxMenuEl.offsetWidth / 2;

  const { left: leftBoundary, right: rightBoundary } = getHorizontalBoundaries(
    toolbarTop,
    ctxMenuEl.offsetHeight,
  );

  const minCenter = leftBoundary + gapToEdge + halfWidth;
  const maxCenter = rightBoundary - gapToEdge - halfWidth;

  // When both panels leave no room, center between them
  if (minCenter > maxCenter) {
    return (leftBoundary + rightBoundary) / 2;
  }

  return Math.min(Math.max(defaultLeft, minCenter), maxCenter);
};

export const getElement = (diagramElementData: GenericObject) => {
  const semanticsId = diagramElementData['semanticsId'];

  const querySelectorId = semanticsId ? `[id="shape-${semanticsId}"]` : '';
  const querySelectorGraphicId = `[data-graphic-id="${diagramElementData['id']}"]`;

  const elementQuerySelector = `${querySelectorId}${querySelectorGraphicId}`;
  const element = document.querySelector(elementQuerySelector);

  if (!element) return;

  return element;
};

/**
 * Creates an element and sets its properties
 * This element is then passed to moveToolbar function
 * to determine the position of quick create menu
 * @param mousePosition - position of the mouse
 * @returns - an object with top and left values that are used for positioning the menu
 */
export const getToolbarPositionBasedOnMousePosition = (
  mousePosition: MenuLocationCoords,
): ToolbarPosition | undefined => {
  // create an element and set its properties
  const el = document.createElement('div');
  el.style.position = 'absolute';
  el.style.left = `${mousePosition.x}px`;
  el.style.top = `${mousePosition.y}px`;
  el.style.visibility = 'hidden';

  document.body.appendChild(el);

  const menuPosition = moveToolbar(
    'quick-create',
    distanceBetweenMouseAndQuickCreateMenu,
    el,
  );

  el.remove();

  return menuPosition;
};

/**
 * Uses the selected element to determine the position of the context menu
 * @param selectedElement - selected element
 * @returns - an object with top and left values that are used for positioning the menu
 */
export const getPositionBasedOnElement = (
  selectedElement: GenericObject | undefined,
): ToolbarPosition | undefined => {
  if (!selectedElement) {
    return { top: '-999px', left: '-999px' };
  }

  const element = getElement(selectedElement);

  return moveToolbar(
    'context-menu',
    distanceBetweenElementAndContextMenu,
    element,
  );
};

/**
 * Uses the hovered element to determine the position of the hyperlink tooltip.
 *
 * @param hoveredElement - identifiers of the hovered graphic (`id` and `semanticsId`)
 * @returns an object with top and left values used for positioning the tooltip,
 * or `undefined` when the graphic is not currently rendered on the canvas
 */
export const getHyperlinkTooltipPosition = (
  hoveredElement: GenericObject,
): ToolbarPosition | undefined =>
  moveToolbar(
    'hyperlink-tooltip',
    distanceBetweenElementAndHyperlinkTooltip,
    getElement(hoveredElement),
  );

export const elementConfigBasedOnQuickCreateEventData = (
  event: GraphActionEvent,
): QuickCreateEntityValue => {
  switch (event.key) {
    case 'createText':
      return { typeName: 'Text' };
    case 'createShape':
    case 'createFormalShape':
      return {
        typeName: 'Box',
        shapeParam: {
          ...event.shapeParam,
        },
      };
    case 'createSticky':
    default:
      return {
        typeName: 'Box',
        shapeParam: {
          shapeType: 'sticker',
        },
      };
  }
};

export const getDefaultFormalItems = (): MenuItem[] => [
  ...getBDCoreObjectTypeSubmenu(),
];
